import React, { useState, useMemo } from 'react';
import './DataTable.css';

function DataTable({ 
  data, 
  columns, 
  searchable = true,
  sortable = true,
  filterable = false,
  filters = {},
  onFilterChange,
  defaultSort = null,
  rowKey = 'id',
  emptyMessage = 'Keine Daten vorhanden',
  getRowClassName = null
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState(defaultSort || { key: null, direction: 'asc' });
  const [activeFilters, setActiveFilters] = useState(filters || {});

  const handleSort = (key) => {
    if (!sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...activeFilters, [key]: value };
    setActiveFilters(newFilters);
    if (onFilterChange) {
      onFilterChange(newFilters);
    }
  };

  const filteredAndSortedData = useMemo(() => {
    let result = [...data];

    if (searchable && searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      result = result.filter(row => {
        return columns.some(col => {
          const value = col.accessor ? col.accessor(row) : row[col.key];
          return value && String(value).toLowerCase().includes(query);
        });
      });
    }

    if (filterable && Object.keys(activeFilters).length > 0) {
      result = result.filter(row => {
        return Object.entries(activeFilters).every(([key, value]) => {
          if (!value || value === '') return true;
          const col = columns.find(c => c.key === key);
          if (!col) return true;
          const rowValue = col.accessor ? col.accessor(row) : row[col.key];
          return String(rowValue).toLowerCase().includes(String(value).toLowerCase());
        });
      });
    }

    if (sortable && sortConfig.key) {
      const col = columns.find(c => c.key === sortConfig.key);
      if (col) {
        result.sort((a, b) => {
          const aVal = col.accessor ? col.accessor(a) : a[sortConfig.key];
          const bVal = col.accessor ? col.accessor(b) : b[sortConfig.key];
          
          if (aVal === null || aVal === undefined) return 1;
          if (bVal === null || bVal === undefined) return -1;
          
          if (col.sortType === 'number') {
            return sortConfig.direction === 'asc' 
              ? (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0)
              : (parseFloat(bVal) || 0) - (parseFloat(aVal) || 0);
          }
          
          if (col.sortType === 'date') {
            return sortConfig.direction === 'asc'
              ? new Date(aVal) - new Date(bVal)
              : new Date(bVal) - new Date(aVal);
          }
          
          const aStr = String(aVal).toLowerCase();
          const bStr = String(bVal).toLowerCase();
          
          if (sortConfig.direction === 'asc') {
            return aStr.localeCompare(bStr, 'de');
          } else {
            return bStr.localeCompare(aStr, 'de');
          }
        });
      }
    }

    return result;
  }, [data, searchQuery, activeFilters, sortConfig, columns, searchable, filterable, sortable]);

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return '⇅';
    }
    return sortConfig.direction === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="data-table-container">
      {searchable && (
        <div className="data-table-search">
          <input
            type="text"
            placeholder="Suchen..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="data-table-search-input"
          />
        </div>
      )}

      {filterable && Object.keys(filters).length > 0 && (
        <div className="data-table-filters">
          {Object.keys(filters).map(filterKey => {
            const col = columns.find(c => c.key === filterKey);
            if (!col || !col.filterable) return null;
            return (
              <div key={filterKey} className="data-table-filter">
                <label>{col.header}:</label>
                <input
                  type="text"
                  value={activeFilters[filterKey] || ''}
                  onChange={(e) => handleFilterChange(filterKey, e.target.value)}
                  placeholder={`${col.header} filtern...`}
                  className="data-table-filter-input"
                />
              </div>
            );
          })}
        </div>
      )}

      <div className="data-table-wrapper">
        <table className="data-table">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`
                    ${sortable && col.sortable !== false ? 'sortable' : ''}
                    ${sortConfig.key === col.key ? `sort-${sortConfig.direction}` : ''}
                    ${col.align ? `align-${col.align}` : ''}
                  `}
                  onClick={() => sortable && col.sortable !== false && handleSort(col.key)}
                  style={{ width: col.width, minWidth: col.minWidth }}
                >
                  <div className="th-content">
                    <span>{col.header}</span>
                    {sortable && col.sortable !== false && (
                      <span className="sort-icon">{getSortIcon(col.key)}</span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="empty-message">
                  {emptyMessage}
                </td>
              </tr>
            ) : (
              filteredAndSortedData.map((row) => (
                <tr 
                  key={row[rowKey] || row.id}
                  className={getRowClassName ? getRowClassName(row) : ''}
                >
                  {columns.map((col) => (
                    <td
                      key={col.key}
                      className={col.align ? `align-${col.align}` : ''}
                    >
                      {col.render ? col.render(row) : (col.accessor ? col.accessor(row) : row[col.key])}
                    </td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {filteredAndSortedData.length > 0 && (
        <div className="data-table-footer">
          <span className="data-table-count">
            {filteredAndSortedData.length} von {data.length} Einträgen
          </span>
        </div>
      )}
    </div>
  );
}

export default DataTable;

